/**
 * Shared interaction layer for Ciari redesign carousels.
 * Layout, navigation and section-specific callbacks stay with each component;
 * this module owns lazy Swiper readiness plus native pointer-drag behavior.
 */
{
  'use strict';

  if (!window.CiariCarousel) {
    const coreScript = document.currentScript || document.querySelector('script[src*="ciari-carousel-core.js"]');
    const swiperSource = coreScript && coreScript.dataset.swiperSrc;
    let swiperPromise;

    const SAFE_INTERACTION = {
      grabCursor: true,
      touchAngle: 30,
      threshold: 8,
      touchStartForcePreventDefault: true,
      touchStartPreventDefault: false,
      touchMoveStopPropagation: false,
      touchReleaseOnEdges: true
    };

    const loadSwiper = () => {
      if (window.Swiper) return Promise.resolve(window.Swiper);
      if (swiperPromise) return swiperPromise;

      swiperPromise = new Promise((resolve, reject) => {
        let vendor = document.querySelector('script[src*="swiper-bundle.min.js"]');
        const onLoad = () => window.Swiper ? resolve(window.Swiper) : reject(new Error('Swiper did not initialize'));

        if (vendor) {
          vendor.addEventListener('load', onLoad, { once: true });
          vendor.addEventListener('error', reject, { once: true });
          return;
        }
        if (!swiperSource) {
          reject(new Error('Swiper asset source is missing'));
          return;
        }

        vendor = document.createElement('script');
        vendor.src = swiperSource;
        vendor.async = true;
        vendor.dataset.ciariSwiper = '';
        vendor.addEventListener('load', onLoad, { once: true });
        vendor.addEventListener('error', reject, { once: true });
        document.head.appendChild(vendor);
      });

      return swiperPromise;
    };

    const whenReady = (callback) => {
      loadSwiper().then(callback).catch(() => {});
    };

    const initializeHorizontalDragScroll = (element, options) => {
      if (!element) return () => {};

      const settings = { threshold: 6, allowTouch: false, moveScroll: true, ...(options || {}) };
      const controller = new AbortController();
      const signal = controller.signal;
      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let startScroll = 0;
      let dragging = false;
      let horizontal = false;
      let suppressClick = false;

      const finish = (event) => {
        if (pointerId === null || (event.pointerId !== undefined && event.pointerId !== pointerId)) return;
        const detail = { startX, startY, endX: event.clientX, endY: event.clientY, dragged: dragging };
        element.classList.remove('is-dragging');
        if (dragging) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 0);
          if (typeof settings.onDragEnd === 'function') settings.onDragEnd(detail);
        }
        pointerId = null;
        dragging = false;
        horizontal = false;
      };

      element.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || (!settings.allowTouch && event.pointerType === 'touch')) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startScroll = element.scrollLeft;
      }, { signal });
      element.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

      element.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (!dragging && Math.max(Math.abs(deltaX), Math.abs(deltaY)) < settings.threshold) return;
        if (!dragging) {
          horizontal = Math.abs(deltaX) > Math.abs(deltaY);
          if (!horizontal) return;
          dragging = true;
          element.classList.add('is-dragging');
          if (element.setPointerCapture) element.setPointerCapture(pointerId);
        }
        if (!horizontal) return;
        event.preventDefault();
        if (settings.moveScroll) element.scrollLeft = startScroll - deltaX;
      }, { signal });

      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => {
        element.addEventListener(type, finish, { signal });
      });
      element.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, signal });

      return () => controller.abort();
    };

    const create = (element, options) => {
      if (!element || !window.Swiper) return null;

      const params = { ...(options || {}), ...SAFE_INTERACTION };
      const wrapper = element.querySelector('.swiper-wrapper');
      if (wrapper) wrapper.style.touchAction = 'pan-y pinch-zoom';

      return new window.Swiper(element, params);
    };

    class CiariCarouselElement extends HTMLElement {
      connectedCallback() {
        this.carouselDisconnected = false;
        whenReady(() => {
          if (!this.carouselDisconnected && this.isConnected) this.init();
        });
      }

      disconnectedCallback() {
        this.carouselDisconnected = true;
        this.destroy();
      }

      get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      motionSpeed(duration) {
        return this.prefersReducedMotion ? 0 : duration;
      }

      autoplayConfig(defaultDelay) {
        if (this.dataset.autoplay !== 'true' || this.prefersReducedMotion) return false;

        return {
          delay: parseInt(this.dataset.autoplayDelay, 10) || defaultDelay,
          pauseOnMouseEnter: true,
          disableOnInteraction: false
        };
      }

      mountCarousel(element, options) {
        this.swiper = create(element, options);
        return this.swiper;
      }

      destroyCarousel() {
        if (!this.swiper) return;
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      destroy() {
        this.destroyCarousel();
      }
    }

    window.CiariCarousel = {
      Element: CiariCarouselElement,
      create,
      initializeHorizontalDragScroll,
      loadSwiper,
      whenReady
    };
  }
}
